/**
 * Identity of a property in the dashboard's filter bar (SL-1709).
 *
 * A dashboard reads from several tables — the main one plus its declared
 * relations — so the filter settings list properties **grouped by table**. That
 * brings a collision the single-table surfaces never had:
 *
 * **Column ids are only unique within their own table.** Two source tables can
 * each hold a `status`, with option lists that overlap in spelling and disagree
 * in meaning; `id` and `name` are worse still, since almost every table has one.
 * The settings draft, the saved config and the bar's own state are all indexed
 * by column id, so two same-named properties from different tables would share
 * **one switch**: turning on "Agent id" would turn on "Interaction id" too.
 *
 * The fix is to qualify the id with the table it came from, and this module is
 * the **only** place that writes or reads that form. Everything downstream — the
 * catalogue, the modal, the config sent to the backend — handles an opaque
 * string and never splits it by hand.
 *
 * ⚠️ **This qualification is a frontend convention; the wire splits it.** The
 * backend stores `tableId` and a plain `columnId` apart and validates both on
 * write (`putDashboardFilterConfig`, SL-1711), so a qualified id would be
 * rejected as a column that does not exist. `filterConfigContract` is the one
 * place that crosses the boundary: `toFilterBarPayload` splits with
 * `splitScopedId`, `toFilterBarConfig` rebuilds with `scopeId`. Nothing else
 * sends or reads the form.
 */

/**
 * The separator.
 *
 * Two characters rather than one because a column id is opaque and may contain
 * a dot or a dash; `::` is not a shape any id in this product takes.
 */
const SEP = '::';

/** `tbl_7` + `col_status` → `tbl_7::col_status`. */
export function scopeId(tableId: string, columnId: string): string {
  return `${tableId}${SEP}${columnId}`;
}

/**
 * The inverse, and it is **total**: it never throws and always returns a column.
 *
 * An id with no separator yields `tableId: null` and the whole string as the
 * column. That is not an error case to guard against — it is every id saved
 * before this convention existed, and it means "resolve it against whichever
 * table is being read". Backward compatibility comes from the codec rather than
 * from a check at each call site.
 *
 * Splits on the **first** separator, so a column id that happens to contain one
 * survives intact. The table id cannot contain one; the column id is opaque and
 * might.
 */
export function splitScopedId(id: string): { tableId: string | null; columnId: string } {
  const at = id.indexOf(SEP);
  if (at < 0) return { tableId: null, columnId: id };
  return { tableId: id.slice(0, at), columnId: id.slice(at + SEP.length) };
}
